import React, { useState, useEffect } from 'react';
import { AppButton, AppSpin, AppTabs, AppFormLabel } from '@/components/atoms';
import { AppTextField } from '@/components/atoms';
import { useAuth } from '@shared/providers/AuthContext';
import { useOptionalSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import {
  usePrefectures,
  useCities,
  useTowns,
  useAddressSearch,
  type AddressContextConfig,
  type City,
  type Town,
  type AddressResult,
} from '@/hooks/useAddressSearch';
import { AppModal } from '@components/atoms';
import useAppToast from '@/hooks/useAppToast';
import { normalizePostalCodeInput } from '@/utils/japaneseText';

const ADDRESS_NOT_FOUND_MESSAGE =
  '該当する住所がありませんでした。もう一度入力しなおしてください。';

const DEFAULT_ADDRESS_RESULT = {
  postal_code1: '101',
  postal_code2: '0063',
  address: '東京都千代田区神田淡路町',
  address_kana: '',
  prefecture_id: undefined,
} as const;

const DEFAULT_ADDRESS_DISPLAY = `〒${DEFAULT_ADDRESS_RESULT.postal_code1}-${DEFAULT_ADDRESS_RESULT.postal_code2} ${DEFAULT_ADDRESS_RESULT.address}`;

export interface ModalAddressResult extends AddressResult {
  town_id?: number;
  isFromAddressSearch?: boolean;
  postalCodeOnlyAutoFill?: boolean;
}
interface AddressSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (result: ModalAddressResult) => void;
  onNoSearchResults?: () => void;
  initialPostalCode1?: string;
  initialPostalCode2?: string;
  initialAddress?: string;
  addressType?: 'patient' | 'insurance';
  title?: string;
  siteType?: string;
  clinicNumber?: string;
  isAdmin?: boolean;
  initialActiveTab?: 'dropdown' | 'search';
  /** Focused after a successful address selection (e.g. 住所2 入力欄). */
  focusAfterSelectRef?: React.RefObject<{ focus?: () => void } | null>;
}

const AddressSearchModal: React.FC<AddressSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onNoSearchResults,
  initialPostalCode1 = '',
  initialPostalCode2 = '',
  initialAddress = '',
  addressType: _addressType = 'patient',
  title,
  siteType: siteTypeProp,
  clinicNumber: clinicNumberProp,
  isAdmin: isAdminProp,
  initialActiveTab = 'dropdown',
  focusAfterSelectRef,
}) => {
  const focusAfterSelect = () => {
    queueMicrotask(() => focusAfterSelectRef?.current?.focus?.());
  };
  const { clinic } = useAuth();
  const toast = useAppToast();
  const [isPreparingInitialSearch, setIsPreparingInitialSearch] = useState(false);

  const handleNoSearchResults = () => {
    if (onNoSearchResults) {
      onNoSearchResults();
      return;
    }

    toast.warning(ADDRESS_NOT_FOUND_MESSAGE);
  };
  const handleNoSearchResultsWithDefaultBehavior = (options?: { fromInitialData?: boolean }) => {
    handleNoSearchResults();
    if (options?.fromInitialData) {
      setIsPreparingInitialSearch(true);
      onClose();
    }
  };

  // Returns undefined when rendered outside SiteAdminAuthProvider (clinic / massager portals)
  const adminCtx = useOptionalSiteAdminAuth();

  // Auto-detect if admin based on context availability or explicit prop
  const isAdmin = isAdminProp ?? !!adminCtx?.currentSite;
  const siteType = siteTypeProp || adminCtx?.currentSite || clinic?.site_type || 'medix';
  const clinicNumber = clinicNumberProp || clinic?.clinic_number || '';

  const addressConfig: AddressContextConfig = {
    siteType,
    clinicNumber: isAdmin ? undefined : clinicNumber,
    isAdmin,
  };

  // React Query hooks
  const { data: prefectures = [], isLoading: isPrefecturesLoading } = usePrefectures(
    addressConfig,
    isOpen
  );
  const citiesMutation = useCities(addressConfig);
  const townsMutation = useTowns(addressConfig);
  const addressSearchMutation = useAddressSearch(addressConfig);

  // Tab state
  const [activeTab, setActiveTab] = useState<'dropdown' | 'search'>(initialActiveTab);

  // Search inputs
  const [postalCode1, setPostalCode1] = useState(initialPostalCode1);
  const [postalCode2, setPostalCode2] = useState(initialPostalCode2);
  const [addressInput, setAddressInput] = useState(initialAddress);

  // Dropdown data
  const [cities, setCities] = useState<City[]>([]);
  const [towns, setTowns] = useState<Town[]>([]);
  const [searchResults, setSearchResults] = useState<Town[]>([]);

  // Selected values
  const [selectedPrefecture, setSelectedPrefecture] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  // Use the town id as the selected value (postal codes may not be unique)
  const [selectedTown, setSelectedTown] = useState<string | null>(null);

  // Display
  const [displayAddress, setDisplayAddress] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<ModalAddressResult | null>(null);
  const hasInitialData = Boolean(
    initialPostalCode1.trim() || initialPostalCode2.trim() || initialAddress.trim()
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      const hasInitialPostal = Boolean(initialPostalCode1.trim() || initialPostalCode2.trim());
      const shouldRunInitialSearch = Boolean(hasInitialPostal || initialAddress);

      setIsPreparingInitialSearch(shouldRunInitialSearch);
      setPostalCode1(initialPostalCode1);
      setPostalCode2(initialPostalCode2);
      setAddressInput(initialAddress);
      setSelectedAddress(null);
      setDisplayAddress(hasInitialData ? DEFAULT_ADDRESS_DISPLAY : '');
      setSearchResults([]);
      setSelectedPrefecture(null);
      setSelectedCity(null);
      setSelectedTown(null);
      setCities([]);
      setTowns([]);
      setActiveTab(shouldRunInitialSearch ? 'search' : initialActiveTab);

      // Auto-search if initial values provided
      if (initialPostalCode1 && initialPostalCode2) {
        handlePostalCodeSearch(initialPostalCode1, initialPostalCode2, undefined, {
          fromInitialData: true,
        });
      } else if (initialPostalCode1 && !initialPostalCode2) {
        // Partial postal code (first 3 digits) - perform search by postal_code1
        handlePostalCodeSearch(initialPostalCode1, '', undefined, {
          fromInitialData: true,
        });
      } else if (initialAddress) {
        // When address exists (including pc2 + address without pc1), run address search
        setAddressInput(initialAddress);
        handleAddressSearch(initialAddress, { fromInitialData: true });
      } else if (!initialPostalCode1 && initialPostalCode2) {
        // Partial postal code (last 4 digits) - let backend validation/filtering handle this case
        handlePostalCodeSearch('', initialPostalCode2, undefined, {
          fromInitialData: true,
        });
      } else {
        setIsPreparingInitialSearch(false);
      }
    }
  }, [isOpen, initialPostalCode1, initialPostalCode2, initialAddress, initialActiveTab]);

  // Handle prefecture selection
  const handlePrefectureChange = (prefId?: number) => {
    // if no prefecture selected, clear dependent state and skip mutation
    if (!prefId || isNaN(prefId)) {
      setSelectedPrefecture(null);
      setCities([]);
      setTowns([]);
      setSelectedCity(null);
      setSelectedTown(null);
      return;
    }

    setSelectedPrefecture(prefId);
    citiesMutation.mutate(prefId, {
      onSuccess: data => {
        setCities(data);
        setTowns([]);
        setSelectedCity(null);
        setSelectedTown(null);

        // Auto-select first city if available
        if (data.length > 0) {
          const firstCity = data[0];
          setSelectedCity(firstCity.id);
          // Fetch towns for the first city
          townsMutation.mutate(firstCity.id, {
            onSuccess: townData => {
              setTowns(townData);
              // Auto-select first town if available
              if (townData.length > 0) {
                const firstTown = townData[0];
                setSelectedTown(String(firstTown.id));
                // Set display address
                const address =
                  firstTown.full_address ||
                  `${firstTown.prefecture_name}${firstTown.city_name}${firstTown.name || ''}`;
                const addressKana = firstTown.full_address_kana;
                setDisplayAddress(
                  `〒${firstTown.postal_code1}-${firstTown.postal_code2} ${address}`
                );
                setSelectedAddress({
                  postal_code1: firstTown.postal_code1,
                  postal_code2: firstTown.postal_code2,
                  address,
                  address_kana: addressKana,
                  prefecture_id: prefId,
                  isFromAddressSearch: false,
                });
              }
            },
            onError: error => {
              console.error('Failed to load towns:', error);
              toast.error('町名の読み込みに失敗しました');
            },
          });
        }
      },
      onError: error => {
        console.error('Failed to load cities:', error);
        toast.error('市区町村の読み込みに失敗しました');
      },
    });
  };

  // Handle city selection
  const handleCityChange = (cityId?: number) => {
    // clear lower selections if nothing chosen
    if (!cityId || isNaN(cityId)) {
      setSelectedCity(null);
      setTowns([]);
      setSelectedTown(null);
      return;
    }

    setSelectedCity(cityId);
    townsMutation.mutate(cityId, {
      onSuccess: data => {
        setTowns(data);
        setSelectedTown(null);

        // Auto-select first town if available
        if (data.length > 0) {
          const firstTown = data[0];
          setSelectedTown(String(firstTown.id));
          // Set display address
          const address =
            firstTown.full_address ||
            `${firstTown.prefecture_name}${firstTown.city_name}${firstTown.name || ''}`;
          const addressKana = firstTown.full_address_kana;
          setDisplayAddress(`〒${firstTown.postal_code1}-${firstTown.postal_code2} ${address}`);
          setSelectedAddress({
            postal_code1: firstTown.postal_code1,
            postal_code2: firstTown.postal_code2,
            address,
            address_kana: addressKana,
            prefecture_id: selectedPrefecture || undefined,
            isFromAddressSearch: false,
          });
        }
      },
      onError: error => {
        console.error('Failed to load towns:', error);
        toast.error('町名の読み込みに失敗しました');
      },
    });
  };

  // Handle town selection
  const handleTownChange = (townId: string) => {
    setSelectedTown(townId);
    const town = towns.find(t => String(t.id) === townId);
    if (town) {
      const address =
        town.full_address || `${town.prefecture_name}${town.city_name}${town.name || ''}`;
      const addressKana = town.full_address_kana;
      setDisplayAddress(`〒${town.postal_code1}-${town.postal_code2} ${address}`);
      setSelectedAddress({
        postal_code1: town.postal_code1,
        postal_code2: town.postal_code2,
        address,
        address_kana: addressKana,
        prefecture_id: selectedPrefecture || undefined,
        isFromAddressSearch: false,
      });
    }
  };

  // Handle search result selection
  const handleResultSelect = (result: Town) => {
    const address =
      result.full_address || `${result.prefecture_name}${result.city_name}${result.name || ''}`;
    const addressKana = result.full_address_kana;
    setDisplayAddress(`〒${result.postal_code1}-${result.postal_code2} ${address}`);
    setSelectedAddress({
      postal_code1: result.postal_code1,
      postal_code2: result.postal_code2,
      address,
      address_kana: addressKana,
      prefecture_id: prefectures.find(p => p.name === result.prefecture_name)?.id,
      town_id: result.id,
      isFromAddressSearch: false,
    });
  };

  // Search by postal code
  const handlePostalCodeSearch = (
    code1?: string,
    code2?: string,
    _overrideAddress?: string,
    options?: { fromInitialData?: boolean }
  ) => {
    const raw1 = code1 ?? postalCode1;
    const raw2 = code2 ?? postalCode2;
    const pc1 = normalizePostalCodeInput(raw1).replace(/\D/g, '').slice(0, 3);
    const pc2 = normalizePostalCodeInput(raw2).replace(/\D/g, '').slice(0, 4);

    // Normalize displayed input values only when search is triggered.
    if (code1 === undefined) {
      setPostalCode1(pc1);
    }
    if (code2 === undefined) {
      setPostalCode2(pc2);
    }

    if (!pc1 && !pc2) {
      setSearchResults([]);
      if (!selectedAddress) {
        setDisplayAddress(hasInitialData ? DEFAULT_ADDRESS_DISPLAY : '');
        setSelectedAddress(null);
      }
      if (options?.fromInitialData) {
        setIsPreparingInitialSearch(false);
      }
      return;
    }

    // Postal search mode: only postal criteria are sent.
    const payload: {
      postal_code1?: string;
      postal_code2?: string;
    } = {};
    if (pc1) payload.postal_code1 = pc1;
    if (pc2) payload.postal_code2 = pc2;

    addressSearchMutation.mutate(payload, {
      onSuccess: data => {
        const hasExistingSelection = !!selectedAddress;

        if (data.length === 0) {
          setSearchResults([]);
          if (!hasExistingSelection) {
            setDisplayAddress(
              options?.fromInitialData || hasInitialData ? DEFAULT_ADDRESS_DISPLAY : ''
            );
            setSelectedAddress(null);
          }
          if (activeTab === 'search' || options?.fromInitialData) {
            handleNoSearchResultsWithDefaultBehavior(options);
          }
          return;
        }

        setSearchResults(data);

        if (data.length === 1) {
          const result = data[0];
          const address =
            result.full_address ||
            `${result.prefecture_name}${result.city_name}${result.name || ''}`;

          onSelect({
            postal_code1: result.postal_code1,
            postal_code2: result.postal_code2,
            address,
            address_kana: result.full_address_kana,
            prefecture_id: prefectures.find(p => p.name === result.prefecture_name)?.id,
            town_id: result.id,
            isFromAddressSearch: false,
          });
          onClose();
          focusAfterSelect();
          return;
        }

        if (!hasExistingSelection) {
          setDisplayAddress(
            data.length >= 2 && (options?.fromInitialData || hasInitialData)
              ? DEFAULT_ADDRESS_DISPLAY
              : ''
          );
          setSelectedAddress(null);
        }
        if (options?.fromInitialData) {
          setIsPreparingInitialSearch(false);
        }
      },
      onError: error => {
        console.error('Failed to search address:', error);
        toast.error('住所検索に失敗しました');
        if (options?.fromInitialData) {
          setIsPreparingInitialSearch(false);
        }
      },
    });
  };

  // Search by address text
  const handleAddressSearch = (
    overrideAddress?: string,
    options?: { fromInitialData?: boolean }
  ) => {
    const query = overrideAddress ?? addressInput;

    if (!query.trim()) {
      setSearchResults([]);
      if (!selectedAddress) {
        setDisplayAddress(hasInitialData ? DEFAULT_ADDRESS_DISPLAY : '');
        setSelectedAddress(null);
      }
      return;
    }

    addressSearchMutation.mutate(
      {
        address: query,
      },
      {
        onSuccess: data => {
          const hasExistingSelection = !!selectedAddress;

          if (data.length === 0) {
            setSearchResults([]);
            if (!hasExistingSelection) {
              if (options?.fromInitialData || hasInitialData) {
                setDisplayAddress(DEFAULT_ADDRESS_DISPLAY);
                setSelectedAddress({ ...DEFAULT_ADDRESS_RESULT, isFromAddressSearch: true });
              } else {
                setDisplayAddress('');
                setSelectedAddress(null);
              }
            }
            if (activeTab === 'search' || options?.fromInitialData) {
              handleNoSearchResultsWithDefaultBehavior(options);
            }
            return;
          }
          setSearchResults(data);
          if (data.length === 1) {
            const result = data[0];
            const address =
              result.full_address ||
              `${result.prefecture_name}${result.city_name}${result.name || ''}`;

            onSelect({
              postal_code1: result.postal_code1,
              postal_code2: result.postal_code2,
              address,
              address_kana: result.full_address_kana,
              prefecture_id: prefectures.find(p => p.name === result.prefecture_name)?.id,
              town_id: result.id,
              isFromAddressSearch: false,
            });
            onClose();
            focusAfterSelect();
          } else {
            if (!hasExistingSelection) {
              // Display default address for initial-data flow only.
              setDisplayAddress(
                options?.fromInitialData || hasInitialData ? DEFAULT_ADDRESS_DISPLAY : ''
              );
              setSelectedAddress(null);
            }
          }
          if (options?.fromInitialData) {
            setIsPreparingInitialSearch(false);
          }
        },
        onError: error => {
          console.error('Failed to search address:', error);
          toast.error('住所検索に失敗しました');
          if (options?.fromInitialData) {
            setIsPreparingInitialSearch(false);
          }
        },
      }
    );
  };

  // Handle OK button
  const handleOk = () => {
    // Allow submission if address is selected OR if displaying default address
    if (selectedAddress) {
      onSelect(selectedAddress);
      onClose();
      focusAfterSelect();
    } else if (displayAddress === DEFAULT_ADDRESS_DISPLAY) {
      // Use default address if that's what's displayed, but find the correct prefecture_id
      const tokyoPrefecture = prefectures.find(p => p.name === '東京都');
      onSelect({
        ...DEFAULT_ADDRESS_RESULT,
        prefecture_id: tokyoPrefecture?.id,
      });
      onClose();
      focusAfterSelect();
    } else {
      toast.warning('住所を選択してください');
      console.warn('⚠️ [AddressSearchModal] No address selected');
    }
  };

  // Determine modal title
  const modalTitle =
    title ||
    (isAdmin ? '住所補助' : _addressType === 'patient' ? '住所補助 (受診者)' : '住所補助 (保険証)');

  // Do not mount AppModal while closed to avoid unnecessary global side effects.
  if (!isOpen) {
    return null;
  }

  // Avoid UI flash while running initial auto-search on open.
  if (isPreparingInitialSearch) {
    return null;
  }

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      maxWidth="max-w-3xl"
      footer={null} // always use our custom footer below
    >
      <AppSpin
        spinning={
          isPrefecturesLoading ||
          citiesMutation.isPending ||
          townsMutation.isPending ||
          addressSearchMutation.isPending
        }
      >
        <AppTabs
          activeKey={activeTab}
          onChange={key => setActiveTab(key as 'dropdown' | 'search')}
          items={[
            {
              key: 'dropdown',
              label: '住所を選択',
              children: (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 h-90">
                    {/* Prefecture Select - Scrollable Listbox */}
                    <div className="flex flex-col">
                      <AppFormLabel className="mb-2" label="都道府県一覧" />
                      <select
                        value={selectedPrefecture ? String(selectedPrefecture) : ''}
                        onChange={e =>
                          handlePrefectureChange(
                            e.target.value ? parseInt(e.target.value) : undefined
                          )
                        }
                        size={15}
                        className="flex-1 w-full p-0 overflow-y-auto bg-white border border-gray-300 rounded"
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="" disabled hidden />
                        {prefectures.map(pref => (
                          <option key={pref.id} value={pref.id}>
                            {pref.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* City Select - Scrollable Listbox */}
                    <div className="flex flex-col">
                      <AppFormLabel className="mb-2" label="市区町村一覧" />
                      <select
                        value={selectedCity ? String(selectedCity) : ''}
                        onChange={e =>
                          handleCityChange(e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        size={15}
                        disabled={!selectedPrefecture}
                        className="flex-1 w-full p-0 overflow-y-auto bg-white border border-gray-300 rounded disabled:bg-gray-100"
                        style={{
                          cursor: selectedPrefecture ? 'pointer' : 'default',
                        }}
                      >
                        <option value="" disabled hidden />
                        {cities.map(city => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Town Select - Scrollable Listbox */}
                    <div className="flex flex-col">
                      <AppFormLabel className="mb-2" label="町域一覧" />
                      <select
                        value={selectedTown || ''}
                        onChange={e => handleTownChange(e.target.value)}
                        size={15}
                        disabled={!selectedCity}
                        className="flex-1 w-full p-0 overflow-y-auto bg-white border border-gray-300 rounded disabled:bg-gray-100"
                        style={{ cursor: selectedCity ? 'pointer' : 'default' }}
                      >
                        <option value="" disabled hidden />
                        {towns.map(town => (
                          <option key={town.id} value={String(town.id)}>
                            {town.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: 'search',
              label: '住所・郵便番号より検索',
              children: (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    {/* Postal Code Search Row */}
                    <div className="flex items-center gap-2">
                      <AppFormLabel className="text-sm font-medium" label="郵便番号" />
                      <AppTextField
                        value={postalCode1}
                        onChange={e => {
                          const newVal = normalizePostalCodeInput(e.target.value)
                            .replace(/\D/g, '')
                            .slice(0, 3);
                          setPostalCode1(newVal);
                        }}
                        maxLength={3}
                        placeholder="000"
                        className="w-20"
                      />
                      <AppTextField
                        value={postalCode2}
                        onChange={e => {
                          const newVal = normalizePostalCodeInput(e.target.value)
                            .replace(/\D/g, '')
                            .slice(0, 4);
                          setPostalCode2(newVal);
                        }}
                        maxLength={4}
                        placeholder="0000"
                        className="w-24"
                      />
                      <AppButton
                        type="primary"
                        onClick={() => handlePostalCodeSearch(undefined, undefined, undefined)}
                      >
                        検索
                      </AppButton>
                    </div>

                    {/* Address Text Search Row */}
                    <div className="flex items-center gap-2">
                      <AppFormLabel className="justify-end w-24 text-sm font-medium" label="住所" />
                      <AppTextField
                        value={addressInput}
                        onChange={e => setAddressInput(e.target.value)}
                        className="flex-1"
                      />
                      <AppButton type="primary" onClick={() => handleAddressSearch()}>
                        検索
                      </AppButton>
                    </div>
                  </div>

                  {/* Search Results - Scrollable Listbox (always visible) */}
                  <div className="flex flex-col">
                    <select
                      value={selectedAddress?.town_id ? String(selectedAddress.town_id) : ''}
                      onChange={e => {
                        const result = searchResults.find(r => String(r.id) === e.target.value);
                        if (result) handleResultSelect(result);
                      }}
                      size={10}
                      className="flex-1 w-full p-2 overflow-y-auto bg-white border border-gray-300 rounded"
                      style={{ cursor: 'pointer', minHeight: '250px' }}
                    >
                      <option value="" disabled hidden />
                      {searchResults.map(result => (
                        <option key={result.id} value={String(result.id)}>
                          〒{result.postal_code1}-{result.postal_code2}{' '}
                          {result.full_address ||
                            `${result.prefecture_name}${result.city_name}${result.name || ''}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <AppFormLabel
                    className="block text-sm font-medium"
                    label="検索結果は100件まで表示します。"
                  />
                </div>
              ),
            },
          ]}
        />

        {/* Display Selected Address - outside tabs */}
        <div className={`flex ${isAdmin ? 'flex-row justify-between items-end' : 'flex-col'}`}>
          <div className={`mt-4 pt-4 space-y-2  ${isAdmin ? 'w-3/5' : 'border-t'}`}>
            <AppTextField value={displayAddress} readOnly className="bg-gray-50" />
          </div>

          {/* Modal action buttons (always visible) */}
          <div
            className={`flex justify-end gap-2 pt-4  mt-4 ${isAdmin ? 'flex-row-reverse' : 'border-t'}`}
          >
            <AppButton
              type="primary"
              onClick={
                !selectedAddress && displayAddress !== DEFAULT_ADDRESS_DISPLAY ? onClose : handleOk
              }
            >
              <i className="mr-2 fa fa-paper-plane" aria-hidden="true"></i>
              適用
            </AppButton>
          </div>
        </div>
      </AppSpin>
    </AppModal>
  );
};

export default AddressSearchModal;
